/**
 * AutoPlacementSection — Event auto-placement settings for an area (ramp/tiedown).
 *
 * Rendered inside the OutdoorAreaForm "Auto-Placement" tab. Binds to the same
 * react-hook-form instance via `ap_*` field names; the parent form assembles
 * these into an AreaPlacementFieldsInput on submit.
 *
 * Authoring layer only — these values are read by a later placement engine.
 * See: docs/018-masters-demo/planemax-event-zone-editor.md
 */

import { useState, type ReactNode } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useTenant } from '@/contexts'
import { useHangars } from '@/hooks/useHangars'
import { useClients } from '@/hooks/useClients'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import {
  AREA_CATEGORIES,
  AREA_CATEGORY_LABELS,
  AUTO_SORTS,
  AUTO_SORT_LABELS,
  TURNOVER_MODES,
  TURNOVER_MODE_LABELS,
  AREA_PARKING_MODES,
  AREA_PARKING_MODE_LABELS,
} from '@/types/area-placement'
import { FieldHelp, type FieldHelpContent } from '@/components/ui/field-help'
import { AREA_PLACEMENT_HELP } from '@/types/area-placement-help'
import { FormationPicker } from '@/components/placement/FormationPicker'
import { DensityControl } from '@/components/placement/DensityControl'
import {
  formationFields,
  deriveFormationKey,
  densityFromFields,
  pitchForDensity,
  DENSITY_HELP,
  type FormationKey,
  type FormationRecipe,
} from '@/lib/placement/formations'
import { AIRCRAFT_CATEGORY_LABELS, type AircraftCategory } from '@/types/aircraft-types'

const SECTION_HEADER =
  'text-sm font-medium text-muted-foreground uppercase tracking-wider'

/** A FormLabel paired with an "(i)" help popover. The icon is a sibling of the
 *  label (not a child) so clicking it never opens the field's own control. */
function LabelWithHelp({
  children,
  help,
  className,
}: {
  children: ReactNode
  help: FieldHelpContent
  className?: string
}) {
  return (
    <div className="flex items-center gap-1.5">
      <FormLabel className={className}>{children}</FormLabel>
      <FieldHelp content={help} />
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyForm = UseFormReturn<any>

interface AutoPlacementSectionProps {
  form: AnyForm
}

export function AutoPlacementSection({ form }: AutoPlacementSectionProps) {
  const { currentLocation } = useTenant()
  const locationId = currentLocation?.id

  // Exit areas at this location (for the taxiway-exit binding select)
  const { data: exitAreas = [] } = useHangars(
    locationId ? { locationId, type: 'exit_point' } : undefined
  )
  // Other areas at this location (for the fallback-area select)
  const { data: locationAreas = [] } = useHangars(
    locationId ? { locationId } : undefined
  )

  const autoOn = !!form.watch('ap_auto_placement')
  const pattern = form.watch('ap_pattern') as string
  const lanes = Number(form.watch('ap_lanes')) || 1
  const turnoverMode = form.watch('ap_turnover_mode') as string
  const parkingMode = form.watch('ap_parking_mode') as string
  const showGeometry = autoOn && !!pattern

  // Formation = the named recipe over pattern/progression/lanes/coupling; one tap
  // sets all four ap_* fields. Density folds pitch mode into a single choice.
  const progression = (form.watch('ap_progression') as FormationRecipe['progression']) ?? 'side_by_side'
  const coupling = (form.watch('ap_coupling') as FormationRecipe['coupling']) ?? null
  const pitchMode = form.watch('ap_pitch_mode') as 'uniform' | 'variable' | undefined
  const formationKey: FormationKey | 'custom' = pattern
    ? deriveFormationKey({ pattern: pattern as FormationRecipe['pattern'], progression, lanes, coupling })
    : 'custom'
  const density = densityFromFields(pitchMode)
  const setFormation = (key: FormationKey) => {
    const r = formationFields(key)
    form.setValue('ap_pattern', r.pattern, { shouldDirty: true })
    form.setValue('ap_progression', r.progression, { shouldDirty: true })
    form.setValue('ap_lanes', r.lanes, { shouldDirty: true })
    form.setValue('ap_coupling', r.coupling, { shouldDirty: true })
  }
  const setDensity = (d: 'comfortable' | 'compact') =>
    form.setValue('ap_pitch_mode', pitchForDensity(d), { shouldDirty: true })

  return (
    <div className="p-4 space-y-5">
      {/* Master toggle */}
      <div className="space-y-3">
        <h3 className={SECTION_HEADER}>Automation</h3>
        <div className="flex items-center justify-between">
          <div>
            <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_auto_placement} className="text-sm">
              Enable auto-placement
            </LabelWithHelp>
            <p className="text-xs text-muted-foreground">
              Allow inbound aircraft to be pre-placed into this area.
            </p>
          </div>
          <FormField
            control={form.control}
            name="ap_auto_placement"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="ap_area_category"
          render={({ field }) => (
            <FormItem>
              <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_area_category}>
                Placement category
              </LabelWithHelp>
              <Select onValueChange={field.onChange} value={field.value ?? ''}>
                <FormControl>
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {AREA_CATEGORIES.filter((c) => c !== 'exit').map((c) => (
                    <SelectItem key={c} value={c}>
                      {AREA_CATEGORY_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {autoOn && (
          <FormField
            control={form.control}
            name="ap_fill_priority"
            render={({ field }) => (
              <FormItem>
                <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_fill_priority}>
                  Fill priority
                </LabelWithHelp>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Lower fills first"
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormDescription>Areas fill in ascending priority order.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {autoOn && (
        <>
          <Separator />

          {/* Active window */}
          <div className="space-y-3">
            <h3 className={SECTION_HEADER}>Active Window</h3>
            <p className="text-xs text-muted-foreground">
              When this area exists on the map and accepts placement.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="ap_active_from"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_active_from} className="text-xs">
                      Active from
                    </LabelWithHelp>
                    <FormControl>
                      <Input type="datetime-local" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ap_active_until"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_active_until} className="text-xs">
                      Active until
                    </LabelWithHelp>
                    <FormControl>
                      <Input type="datetime-local" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="ap_release_from"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_release_from} className="text-xs">
                      Release from (optional)
                    </LabelWithHelp>
                    <FormControl>
                      <Input type="datetime-local" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ap_release_until"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_release_until} className="text-xs">
                      Release until (optional)
                    </LabelWithHelp>
                    <FormControl>
                      <Input type="datetime-local" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator />

          {/* Eligibility — the smart filter */}
          <div className="space-y-3">
            <h3 className={SECTION_HEADER}>Eligibility Filter</h3>
            <FormField
              control={form.control}
              name="ap_enforcement"
              render={({ field }) => (
                <FormItem>
                  <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_enforcement}>
                    Enforcement
                  </LabelWithHelp>
                  <Select onValueChange={field.onChange} value={field.value ?? 'hard'}>
                    <FormControl>
                      <SelectTrigger className="w-full min-w-0">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="hard">Hard (robot never overrides)</SelectItem>
                      <SelectItem value="soft">Soft (nudge / preference)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Stay profile — drop-and-go vs overnight parking */}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="ap_turnover_mode"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_turnover_mode} className="text-xs">
                      Stay profile
                    </LabelWithHelp>
                    <Select onValueChange={field.onChange} value={field.value ?? 'mixed'}>
                      <FormControl>
                        <SelectTrigger className="w-full min-w-0">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TURNOVER_MODES.map((m) => (
                          <SelectItem key={m} value={m}>
                            {TURNOVER_MODE_LABELS[m]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              {turnoverMode !== 'parking' && (
                <FormField
                  control={form.control}
                  name="ap_dwell_max_minutes"
                  render={({ field }) => (
                    <FormItem>
                      <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_dwell_max_minutes} className="text-xs">
                        Max stay (min)
                      </LabelWithHelp>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="e.g. 180"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Parking / towing — self-taxi vs tug to the spot */}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="ap_parking_mode"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_parking_mode} className="text-xs">
                      Parking mode
                    </LabelWithHelp>
                    <Select onValueChange={field.onChange} value={field.value ?? 'both'}>
                      <FormControl>
                        <SelectTrigger className="w-full min-w-0">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AREA_PARKING_MODES.map((m) => (
                          <SelectItem key={m} value={m}>
                            {AREA_PARKING_MODE_LABELS[m]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              {parkingMode === 'both' && (
                <FormField
                  control={form.control}
                  name="ap_tow_min_wingspan_ft"
                  render={({ field }) => (
                    <FormItem>
                      <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_tow_min_wingspan_ft} className="text-xs">
                        Tow if wingspan ≥ (ft)
                      </LabelWithHelp>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="e.g. 60"
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="ap_max_wingspan_ft"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_max_wingspan_ft} className="text-xs">
                      Max wingspan (ft)
                    </LabelWithHelp>
                    <FormControl>
                      <Input type="number" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ap_max_length_ft"
                render={({ field }) => (
                  <FormItem>
                    <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_max_length_ft} className="text-xs">
                      Max length (ft)
                    </LabelWithHelp>
                    <FormControl>
                      <Input type="number" {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Aircraft type eligibility — route jets / props to their own areas */}
            <FormField
              control={form.control}
              name="ap_allowed_categories"
              render={({ field }) => (
                <FormItem>
                  <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_allowed_categories}>
                    Eligible aircraft types
                  </LabelWithHelp>
                  <AircraftCategoryMultiSelect
                    value={(field.value as string[]) ?? []}
                    onChange={field.onChange}
                  />
                  <FormDescription>
                    Empty = all types. Pick categories to route by type (e.g. jets here, pistons elsewhere).
                  </FormDescription>
                </FormItem>
              )}
            />

            {/* VIP — allowed clients */}
            <FormField
              control={form.control}
              name="ap_allowed_client_ids"
              render={({ field }) => (
                <FormItem>
                  <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_allowed_client_ids}>
                    Allowed clients (VIP area)
                  </LabelWithHelp>
                  <ClientMultiSelect
                    value={(field.value as string[]) ?? []}
                    onChange={field.onChange}
                  />
                  <FormDescription>
                    Empty = open to all. Set to reserve this area for specific clients.
                  </FormDescription>
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Formation — one tap sets pattern + progression + lanes + coupling */}
          <div className="space-y-3">
            <h3 className={SECTION_HEADER}>Formation</h3>
            <FormationPicker value={formationKey} onChange={setFormation} />
            {formationKey === 'custom' && !!pattern && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Custom layout — authored via raw fields; pick a tile to standardize.
              </p>
            )}
          </div>

          {showGeometry && (
            <>
              <Separator />

              {/* Geometry */}
              <div className="space-y-3">
                <h3 className={SECTION_HEADER}>Geometry</h3>

                <FormField
                  control={form.control}
                  name="ap_placement_heading"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <FormLabel className="text-xs">Placement heading</FormLabel>
                          <FieldHelp content={AREA_PLACEMENT_HELP.ap_placement_heading} />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {Math.round(Number(field.value) || 0)}°
                        </span>
                      </div>
                      <FormControl>
                        <Slider
                          min={0}
                          max={359}
                          step={1}
                          value={[Number(field.value) || 0]}
                          onValueChange={([v]) => field.onChange(v)}
                        />
                      </FormControl>
                      <FormDescription>Direction the noses point when auto-placed.</FormDescription>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-3">
                  <FormItem>
                    <div className="flex items-center gap-1.5">
                      <FormLabel>Density</FormLabel>
                      <FieldHelp content={DENSITY_HELP} />
                    </div>
                    <FormControl>
                      <DensityControl value={density} onChange={setDensity} />
                    </FormControl>
                  </FormItem>
                  <FormField
                    control={form.control}
                    name="ap_safety_margin_ft"
                    render={({ field }) => (
                      <FormItem>
                        <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_safety_margin_ft}>
                          Safety margin (ft)
                        </LabelWithHelp>
                        <FormControl>
                          <Input type="number" step="0.5" {...field} value={field.value ?? ''} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="ap_auto_sort"
                  render={({ field }) => (
                    <FormItem>
                      <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_auto_sort}>
                        Auto-sort
                      </LabelWithHelp>
                      <Select onValueChange={field.onChange} value={field.value ?? 'balanced'}>
                        <FormControl>
                          <SelectTrigger className="w-full min-w-0">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {AUTO_SORTS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {AUTO_SORT_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              {/* Routing & overflow */}
              <div className="space-y-3">
                <h3 className={SECTION_HEADER}>Routing &amp; Overflow</h3>
                <FormField
                  control={form.control}
                  name="ap_taxiway_exit_point_id"
                  render={({ field }) => (
                    <FormItem>
                      <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_taxiway_exit_point_id}>
                        Taxiway exit
                      </LabelWithHelp>
                      <Select onValueChange={field.onChange} value={field.value ?? ''}>
                        <FormControl>
                          <SelectTrigger className="w-full min-w-0">
                            <SelectValue placeholder="Nearest (auto)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {exitAreas.length === 0 && (
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">
                              No exit areas yet — add a Runway Exit area.
                            </div>
                          )}
                          {exitAreas.map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ap_fallback_area_id"
                  render={({ field }) => (
                    <FormItem>
                      <LabelWithHelp help={AREA_PLACEMENT_HELP.ap_fallback_area_id}>
                        Fallback area (overflow)
                      </LabelWithHelp>
                      <Select onValueChange={field.onChange} value={field.value ?? ''}>
                        <FormControl>
                          <SelectTrigger className="w-full min-w-0">
                            <SelectValue placeholder="Next by priority (default)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locationAreas.map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Client multi-select (VIP allow-list)
// ---------------------------------------------------------------------------

function clientLabel(c: {
  company_name: string | null
  first_name: string | null
  last_name: string | null
}): string {
  return (
    c.company_name ||
    [c.first_name, c.last_name].filter(Boolean).join(' ') ||
    'Unnamed client'
  )
}

interface ClientMultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
}

function ClientMultiSelect({ value, onChange }: ClientMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const { data: clients = [] } = useClients()

  const selected = clients.filter((c) => value.includes(c.id))

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal"
          >
            {value.length > 0 ? `${value.length} client(s) selected` : 'Any client'}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search clients..." />
            <CommandList>
              <CommandEmpty>No clients found.</CommandEmpty>
              <CommandGroup>
                {clients.map((c) => (
                  <CommandItem key={c.id} value={clientLabel(c)} onSelect={() => toggle(c.id)}>
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        value.includes(c.id) ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    {clientLabel(c)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((c) => (
            <Badge key={c.id} variant="secondary" className="gap-1">
              {clientLabel(c)}
              <button type="button" onClick={() => toggle(c.id)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Aircraft-category multi-select (type eligibility — jets vs props/rotor)
// ---------------------------------------------------------------------------

/** Categories grouped for the picker — mirrors how operators think about them. */
const CATEGORY_GROUPS: { label: string; categories: AircraftCategory[] }[] = [
  {
    label: 'Jets',
    categories: [
      'very_light_jet',
      'light_jet',
      'midsize_jet',
      'super_midsize_jet',
      'large_jet',
      'ultra_long_range',
    ],
  },
  {
    label: 'Props & Rotorcraft',
    categories: ['turboprop', 'single_engine_piston', 'multi_engine_piston', 'helicopter'],
  },
]

interface AircraftCategoryMultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
}

function AircraftCategoryMultiSelect({ value, onChange }: AircraftCategoryMultiSelectProps) {
  const [open, setOpen] = useState(false)

  const toggle = (cat: string) => {
    onChange(value.includes(cat) ? value.filter((v) => v !== cat) : [...value, cat])
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal"
          >
            {value.length > 0 ? `${value.length} type(s) selected` : 'Any type'}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search types..." />
            <CommandList>
              <CommandEmpty>No types found.</CommandEmpty>
              {CATEGORY_GROUPS.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {group.categories.map((cat) => (
                    <CommandItem
                      key={cat}
                      value={AIRCRAFT_CATEGORY_LABELS[cat]}
                      onSelect={() => toggle(cat)}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          value.includes(cat) ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      {AIRCRAFT_CATEGORY_LABELS[cat]}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((cat) => (
            <Badge key={cat} variant="secondary" className="gap-1">
              {AIRCRAFT_CATEGORY_LABELS[cat as AircraftCategory] ?? cat}
              <button type="button" onClick={() => toggle(cat)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
